import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Sanitize filename
const sanitize = (name) => name.replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_').substring(0, 80);

// Helper to format values safely
const formatVal = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') return val.toLocaleString('id-ID');
    return String(val);
};

// Robust download trigger using hidden <a> tag
const triggerDownload = (blob, filename) => {
    try {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 100);
        console.log(`Download triggered for: ${filename}`);
    } catch (err) {
        console.error('Download Trigger Error:', err);
        throw new Error('Gagal memicu unduhan browser.');
    }
};

/**
 * Export dataset ke XLSX
 */
export const exportToXLSX = (dataset) => {
  try {
    const filename = `${sanitize(dataset.judul)}.xlsx`;
    const wb = XLSX.utils.book_new();

    // Sheet 1: Info dataset
    const infoData = [
        ['NGADHU - Portal Satu Data Kabupaten Ngada'],
        [''],
        ['Judul', dataset.judul],
        ['Deskripsi', dataset.deskripsi],
        ['Kategori', dataset.kategori],
        ['Organisasi', dataset.organisasi],
        ['Satuan', dataset.satuan || '-'],
        ['Sumber Data', dataset.sumberData || '-'],
        ['Periode', `${dataset.tahunMulai || ''} - ${dataset.tahunAkhir || ''}`],
        ['Nilai Terakhir', formatVal(dataset.nilaiTerakhir)],
        ['Tanggal Export', new Date().toLocaleDateString('id-ID')],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
    XLSX.utils.book_append_sheet(wb, wsInfo, 'Informasi');

    // Sheet 2: DataPoints (jika ada)
    if (dataset.dataPoints && dataset.dataPoints.length > 0) {
        const dpHeaders = ['Tahun', 'Nilai', 'Kecamatan', 'Keterangan'];
        const dpData = dataset.dataPoints.map(dp => [dp.tahun, dp.nilai, dp.kecamatan || '', dp.keterangan || '']);
        const wsDP = XLSX.utils.aoa_to_sheet([dpHeaders, ...dpData]);
        XLSX.utils.book_append_sheet(wb, wsDP, 'Data Poin');
    }

    // Sheet 3: Table Data (jika ada)
    if (dataset.tableColumns && dataset.tableColumns.length > 0 && dataset.tableRows && dataset.tableRows.length > 0) {
        const headers = dataset.tableColumns.map(c => c.label);
        const keys = dataset.tableColumns.map(c => c.key);
        const rows = dataset.tableRows.map(r => keys.map(k => r.data?.[k] ?? ''));
        const wsTable = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(wb, wsTable, 'Tabel Data');
    }

    // Use XLSX.writeFile directly for better browser compatibility
    XLSX.writeFile(wb, filename);
    console.log('XLSX.writeFile initiated successfully');
  } catch (err) {
    console.error('XLSX Export Error:', err);
    throw err;
  }
};

/**
 * Export dataset ke PDF
 */
export const exportToPDF = (dataset) => {
  try {
    const filename = `${sanitize(dataset.judul)}.pdf`;
    const doc = new jsPDF();

    // Header
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('NGADHU - Portal Satu Data Kabupaten Ngada', 14, 15);
    doc.setDrawColor(50, 150, 108);
    doc.line(14, 18, 196, 18);

    // Title
    doc.setFontSize(14);
    doc.setTextColor(20, 58, 42);
    const splitTitle = doc.splitTextToSize(dataset.judul, 160);
    doc.text(splitTitle, 14, 28);
    let y = 28 + (splitTitle.length * 7);

    // Info
    doc.setFontSize(9);
    doc.setTextColor(80);
    const info = [
        `Kategori: ${dataset.kategori} | Organisasi: ${dataset.organisasi}`,
        `Satuan: ${dataset.satuan || '-'} | Periode: ${dataset.tahunMulai || ''}-${dataset.tahunAkhir || ''}`
    ];
    info.forEach(line => { doc.text(line, 14, y); y += 5; });

    // Table
    if (dataset.dataPoints && dataset.dataPoints.length > 0) {
      autoTable(doc, {
        startY: y + 5,
        head: [['Tahun', 'Nilai', 'Kecamatan', 'Keterangan']],
        body: dataset.dataPoints.map(dp => [dp.tahun, formatVal(dp.nilai), dp.kecamatan || '-', dp.keterangan || '-']),
        headStyles: { fillColor: [50, 150, 108] },
        styles: { fontSize: 8 }
      });
    } else if (dataset.tableColumns?.length > 0) {
      const headers = dataset.tableColumns.map(c => c.label);
      const keys = dataset.tableColumns.map(c => c.key);
      const rows = dataset.tableRows?.map(r => keys.map(k => formatVal(r.data?.[k]))) || [];
      autoTable(doc, {
        startY: y + 5,
        head: [headers],
        body: rows,
        headStyles: { fillColor: [50, 150, 108] },
        styles: { fontSize: 8 }
      });
    }

    // Save
    doc.save(filename);
    console.log('PDF doc.save initiated successfully');
  } catch (err) {
    console.error('PDF Export Error:', err);
    throw err;
  }
};

/**
 * Export dataset ke JSON
 */
export const exportToJSON = (dataset) => {
  try {
    const filename = `${sanitize(dataset.judul)}.json`;
    const exportData = {
        metadata: {
            judul: dataset.judul,
            organisasi: dataset.organisasi,
            tanggalExport: new Date().toISOString()
        },
        data: dataset.dataPoints || [],
        table: {
            columns: dataset.tableColumns || [],
            rows: dataset.tableRows?.map(r => r.data) || []
        }
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    triggerDownload(blob, filename);
  } catch (err) {
    console.error('JSON Export Error:', err);
    throw err;
  }
};
